import React, { useEffect, useState } from "react";
import { brandingApi } from "../api/client";

// I² brand assets. Both cropped directly from the real uploaded lockup at
// public/I_square_product_logo.png — not redrawn recreations.
//
// Two variants:
// - "full" (default): the complete lockup — I2 mark + MIRRORSME wordmark +
//   triangle icon + tagline. This is the actual logo; use it everywhere
//   there's room for it (topbar, login, marketing surfaces).
// - "mark": just the I2 glyph, isolated from the wordmark. Small-format
//   only (favicon) — the wordmark disappears at favicon sizes anyway.
//
// Each variant ships in two ink colors, both cropped from the identical
// source pixels: the natural near-black ink (for light/neutral
// backgrounds) and a white-ink version (for dark surfaces / saturated
// tiles where the dark ink has no contrast).
const SOURCES = {
  full: {
    dark: "/i2-logo-full.png",
    white: "/i2-logo-full-white.png",
  },
  mark: {
    dark: "/i2-mark.png",
    white: "/i2-mark-white.png",
  },
};

// Natural aspect ratio (width:height) of each variant's crop.
const ASPECT = {
  full: 287 / 231,
  mark: 99 / 226,
};

// Per-tenant logo override (Admin > Branding). `hasCustomLogo` comes from
// currentTenant.has_custom_logo (set post-login only — the pre-login
// screen always shows the default I² lockup, see LoginPage.jsx callers).
// The endpoint is JWT-gated, so a bare <img src> can't reach it (no
// Authorization header on image requests) — fetch the bytes and hand the
// <img> a blob: URL instead, same pattern as ChatCitations.jsx's
// PageImageWithFallback / voiceQaApi.fetchAudioBlobUrl.
function useCustomLogoBlobUrl(hasCustomLogo) {
  const [blobUrl, setBlobUrl] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    if (!hasCustomLogo) {
      setBlobUrl(null);
      setFailed(false);
      return undefined;
    }
    let cancelled = false;
    let objectUrl = null;
    setFailed(false);

    brandingApi.fetchLogoBlobUrl()
      .then((url) => {
        if (cancelled) return;
        objectUrl = url;
        setBlobUrl(url);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [hasCustomLogo]);

  return failed ? null : blobUrl;
}

export default function Logo({ variant = "full", size = 44, tone = "dark", alt = "I² Platform", hasCustomLogo = false, style, ...rest }) {
  const customLogoUrl = useCustomLogoBlobUrl(hasCustomLogo);

  if (customLogoUrl) {
    return (
      <img
        src={customLogoUrl}
        alt={alt}
        style={{ display: "block", height: size, width: "auto", maxWidth: size * 3, objectFit: "contain", ...style }}
        onError={(e) => { e.currentTarget.style.display = "none"; }}
        {...rest}
      />
    );
  }

  const src = (SOURCES[variant] || SOURCES.full)[tone] || SOURCES[variant].dark;
  const width = Math.round(size * (ASPECT[variant] || ASPECT.full));
  return (
    <img
      src={src}
      alt={alt}
      width={width}
      height={size}
      style={{ display: "block", objectFit: "contain", ...style }}
      {...rest}
    />
  );
}
